import { AlertTriangle, ArrowLeft, CheckCircle2, Clock, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Container } from '../../components/ui/Container';
import { PageHero } from '../../components/ui/PageHero';
import { pb } from '../../lib/pb';
import { useDialog } from '../../lib/useDialog';

type Tab = 'open' | 'fulfilled';

interface RequesterExpand {
  id: string;
  name: string;
  email: string;
  unit_number: string | null;
}

interface RecordsRequest {
  id: string;
  requester: string;
  categories: string[] | null;
  notes: string;
  status: 'open' | 'fulfilled' | 'declined';
  opened_at: string;
  resolved_at: string;
  resolution_notes: string;
  created: string;
  expand?: { requester?: RequesterExpand };
}

function daysSince(iso: string): number {
  const ms = Date.now() - new Date(iso).getTime();
  return Math.floor(ms / (24 * 60 * 60 * 1000));
}

function formatDate(iso: string): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function categoriesLabel(cats: string[] | null | undefined): string {
  if (!cats || cats.length === 0) return '(none specified)';
  return cats.join(' · ');
}

export function AdminRecordsRequests(): JSX.Element {
  const [tab, setTab] = useState<Tab>('open');
  const [open, setOpen] = useState<RecordsRequest[] | null>(null);
  const [fulfilled, setFulfilled] = useState<RecordsRequest[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const { prompt, alert, dialog } = useDialog();

  useEffect(() => {
    const previous = document.title;
    document.title = 'Admin · Records Requests | Sunset Harbor';
    return () => {
      document.title = previous;
    };
  }, []);

  async function loadOpen(): Promise<void> {
    setLoadError(null);
    try {
      const result = await pb.collection('records_requests').getList<RecordsRequest>(1, 100, {
        filter: 'status = "open"',
        sort: 'created',
        expand: 'requester',
      });
      setOpen(result.items);
    } catch (err) {
      console.error(err);
      setLoadError('Could not load open requests.');
    }
  }

  async function loadFulfilled(): Promise<void> {
    setLoadError(null);
    try {
      const cutoff = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString();
      const result = await pb.collection('records_requests').getList<RecordsRequest>(1, 100, {
        filter: `status != "open" && resolved_at >= "${cutoff}"`,
        sort: '-resolved_at',
        expand: 'requester',
      });
      setFulfilled(result.items);
    } catch (err) {
      console.error(err);
      setLoadError('Could not load fulfilled requests.');
    }
  }

  useEffect(() => {
    void loadOpen();
    void loadFulfilled();
  }, []);

  async function markFulfilled(r: RecordsRequest): Promise<void> {
    const notes = await prompt(
      `Resolution notes for ${r.expand?.requester?.name ?? 'this request'} (optional, visible to the owner):`,
      '',
    );
    if (notes === null) return; // user cancelled
    try {
      await pb.collection('records_requests').update(r.id, {
        status: 'fulfilled',
        resolved_at: new Date().toISOString(),
        resolution_notes: notes,
      });
      await loadOpen();
      await loadFulfilled();
    } catch (err) {
      console.error(err);
      await alert('Update failed. Refresh and try again.');
    }
  }

  async function decline(r: RecordsRequest): Promise<void> {
    const reason = await prompt(
      `Reason for declining ${r.expand?.requester?.name ?? 'this request'} (required, visible to the owner):`,
      '',
    );
    if (reason === null) return;
    if (!reason.trim()) {
      await alert('A reason is required to decline.');
      return;
    }
    try {
      await pb.collection('records_requests').update(r.id, {
        status: 'declined',
        resolved_at: new Date().toISOString(),
        resolution_notes: reason.trim(),
      });
      await loadOpen();
      await loadFulfilled();
    } catch (err) {
      console.error(err);
      await alert('Update failed. Refresh and try again.');
    }
  }

  function daysBadge(openedAt: string): JSX.Element {
    const d = daysSince(openedAt);
    let classes = 'bg-brand-surface text-brand-body-dark';
    let label = `${d} day${d === 1 ? '' : 's'} open`;
    if (d >= 9) {
      classes = 'bg-red-100 text-red-800 font-semibold';
      label = `${d} days, past statute window`;
    } else if (d >= 5) {
      classes = 'bg-yellow-100 text-yellow-800 font-semibold';
      label = `${d} days, approaching deadline`;
    }
    return (
      <span className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs ${classes}`}>
        <Clock size={12} aria-hidden="true" />
        {label}
      </span>
    );
  }

  return (
    <>
      {dialog}
      <PageHero
        eyebrow="Admin"
        title="Records-request queue"
        lead="Owner records requests submitted under Florida Statute 718.111(12)(c). The statute requires a response within ten working days."
      />

      <section className="section bg-brand-white">
        <Container>
          <div className="mb-8">
            <Link
              to="/portal"
              className="inline-flex items-center gap-2 text-sm font-semibold text-brand-charcoal underline decoration-brand-yellow-deep/40 underline-offset-4 hover:decoration-brand-yellow-deep"
            >
              <ArrowLeft size={14} aria-hidden="true" />
              Back to portal
            </Link>
          </div>

          {/* Tabs */}
          <div className="mb-8 border-b border-brand-surface">
            <nav className="-mb-px flex gap-6" aria-label="Records request status">
              {(['open', 'fulfilled'] as const).map((t) => {
                const count = t === 'open' ? open?.length : fulfilled?.length;
                const active = tab === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTab(t)}
                    className={`-mb-px border-b-2 px-1 py-3 text-sm font-semibold transition-colors ${
                      active
                        ? 'border-brand-yellow-deep text-brand-charcoal'
                        : 'border-transparent text-brand-body-medium hover:text-brand-charcoal'
                    }`}
                  >
                    {t === 'open' ? 'Open' : 'Fulfilled & Declined (last 90 days)'}
                    {typeof count === 'number' ? (
                      <span className="ml-2 rounded bg-brand-surface px-2 py-0.5 text-xs text-brand-body-dark">
                        {count}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </nav>
          </div>

          {loadError ? (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
            >
              {loadError}
            </div>
          ) : null}

          {tab === 'open' ? (
            open === null ? (
              <p className="text-sm text-brand-body-medium">Loading...</p>
            ) : open.length === 0 ? (
              <div className="rounded-lg border border-dashed border-brand-surface bg-brand-surface/30 px-6 py-12 text-center">
                <CheckCircle2
                  size={28}
                  strokeWidth={1.5}
                  aria-hidden="true"
                  className="mx-auto mb-3 text-brand-body-medium"
                />
                <p className="text-sm text-brand-body-medium">
                  No open requests. New ones appear here when owners submit through the contact form
                  with category &ldquo;Records request&rdquo;.
                </p>
              </div>
            ) : (
              <ul className="space-y-4">
                {open.map((r) => (
                  <li
                    key={r.id}
                    className="rounded-lg border border-brand-surface bg-brand-white p-6 shadow-card"
                  >
                    <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-base font-bold text-brand-charcoal">
                            {r.expand?.requester?.name ?? '(unknown requester)'}
                          </p>
                          {r.expand?.requester?.unit_number ? (
                            <span className="rounded bg-brand-surface px-2 py-0.5 text-xs text-brand-body-dark">
                              Unit {r.expand.requester.unit_number}
                            </span>
                          ) : null}
                          {daysBadge(r.created)}
                        </div>
                        <p className="mt-2 text-sm text-brand-body-medium">
                          {r.expand?.requester?.email ?? ''} · Opened {formatDate(r.created)}
                        </p>
                        <div className="mt-3">
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-body-medium">
                            Categories requested
                          </p>
                          <p className="mt-1 text-sm text-brand-charcoal">
                            {categoriesLabel(r.categories)}
                          </p>
                        </div>
                        {r.notes ? (
                          <div className="mt-3">
                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-body-medium">
                              Owner notes
                            </p>
                            <p className="mt-1 whitespace-pre-line text-sm text-brand-body-dark">
                              {r.notes}
                            </p>
                          </div>
                        ) : null}
                      </div>
                      <div className="flex shrink-0 flex-col gap-2 md:w-48">
                        <button
                          type="button"
                          onClick={() => void markFulfilled(r)}
                          className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-brand-yellow px-4 text-sm font-semibold text-brand-charcoal transition-colors hover:bg-brand-yellow-deep"
                        >
                          <CheckCircle2 size={16} aria-hidden="true" />
                          Mark fulfilled
                        </button>
                        <button
                          type="button"
                          onClick={() => void decline(r)}
                          className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-brand-surface px-4 text-sm font-semibold text-brand-body-dark transition-colors hover:bg-brand-surface"
                        >
                          <XCircle size={16} aria-hidden="true" />
                          Decline
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )
          ) : fulfilled === null ? (
            <p className="text-sm text-brand-body-medium">Loading...</p>
          ) : fulfilled.length === 0 ? (
            <p className="text-sm text-brand-body-medium">
              No fulfilled or declined requests in the last 90 days.
            </p>
          ) : (
            <ul className="space-y-3">
              {fulfilled.map((r) => (
                <li
                  key={r.id}
                  className="rounded-lg border border-brand-surface bg-brand-white p-5 shadow-card"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-base font-bold text-brand-charcoal">
                      {r.expand?.requester?.name ?? '(unknown)'}
                    </p>
                    {r.expand?.requester?.unit_number ? (
                      <span className="rounded bg-brand-surface px-2 py-0.5 text-xs text-brand-body-dark">
                        Unit {r.expand.requester.unit_number}
                      </span>
                    ) : null}
                    {r.status === 'fulfilled' ? (
                      <span className="inline-flex items-center gap-1 rounded bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800">
                        <CheckCircle2 size={12} aria-hidden="true" />
                        Fulfilled
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">
                        <AlertTriangle size={12} aria-hidden="true" />
                        Declined
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-brand-body-medium">
                    Opened {formatDate(r.created)} · Resolved {formatDate(r.resolved_at)}
                  </p>
                  <p className="mt-2 text-sm text-brand-charcoal">
                    Categories: {categoriesLabel(r.categories)}
                  </p>
                  {r.resolution_notes ? (
                    <p className="mt-2 whitespace-pre-line text-sm text-brand-body-dark">
                      <span className="font-semibold">Resolution: </span>
                      {r.resolution_notes}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </Container>
      </section>
    </>
  );
}
